/**
 * Pure roster localization. Two independent strategies, applied in order:
 *
 *  1. Per-id overlay (the hand-written `role`/`task` pairs in
 *     `content/ru/agents.ts`, keyed by the 14 static fallback ids) — full
 *     control, used verbatim when present. This covers the OFFLINE fallback
 *     registry and nothing else: those 14 rows are the site's own copy about
 *     its own working structure, so they may be written freely.
 *
 *  2. Translation of what the ENGINE reports, for every observer-backed row
 *     (`N0001…`, ids unknowable ahead of time). Two fields, both derived,
 *     neither invented:
 *       - `role` — the engine states `NANOAGENT · GEN <n>`; the Russian
 *         rendering states the same thing in Russian. A NanoAgent is
 *         role-neutral by construction (see live-fold.ts's header), so a
 *         kind-derived job title such as «Исследовательский интеллект» would
 *         be a claim the engine never made. That fallback used to exist here
 *         and is deliberately gone.
 *       - `task` — rendered from the row's structured `phrase`
 *         (`task-phrase.ts`), which is the same statement as the English
 *         `task` string, minus the English. A row without a phrase keeps its
 *         `task` exactly as received.
 *
 * Pure module: no Next/React/content imports — importable directly under
 * `node --test --experimental-strip-types`. `content/i18n.ts` is the only
 * caller and supplies the concrete overlay table.
 */

import type { LabAgent } from './types.ts';
import type { Lang } from '../i18n/config';
import { renderTaskPhrase } from './task-phrase.ts';

export interface AgentOverlay {
  role?: string;
  task?: string;
}

/** How each locale names the engine's own role statement `NANOAGENT · GEN n`. */
const NANOAGENT_ROLE: Record<Lang, (generation: number) => string> = {
  en: (g) => `NANOAGENT · GEN ${g}`,
  ru: (g) => `НАНОАГЕНТ · ПОКОЛЕНИЕ ${g}`,
};

/** The English form live-fold writes — the only role string safe to restate. */
const ENGINE_ROLE_RE = /^NANOAGENT · GEN (\d{1,9})$/;

/**
 * Translate the engine-stated role of one observer-backed row, or return it
 * untouched. `generation` is the engine's own counter and is preferred; the
 * string form is parsed only as a fallback, and anything that is not the
 * engine's exact phrasing is left alone rather than guessed at.
 */
export function localizeEngineRole(agent: LabAgent, lang: Lang): string {
  const fromString = ENGINE_ROLE_RE.exec(agent.role);
  if (fromString === null) return agent.role;
  const generation =
    typeof agent.generation === 'number' && Number.isSafeInteger(agent.generation)
      ? agent.generation
      : Number(fromString[1]);
  return NANOAGENT_ROLE[lang](generation);
}

export function localizeAgentRoster(
  agents: readonly LabAgent[],
  overlay: Readonly<Record<string, AgentOverlay>>,
  lang: Lang,
): LabAgent[] {
  return agents.map((a) => {
    const o = overlay[a.id];
    if (o !== undefined) {
      return { ...a, role: o.role ?? a.role, task: o.task ?? a.task };
    }
    const role = localizeEngineRole(a, lang);
    const task = a.phrase === undefined ? a.task : renderTaskPhrase(lang, a.phrase);
    return role === a.role && task === a.task ? a : { ...a, role, task };
  });
}
